import { useMembership } from '../context/MembershipContext';
import { useDataUser } from '../context/dataUserContext';
import { getFeaturesForRoleAndPlan, hasFeatureAccess as checkFeatureAccess } from '../config/membershipFeatures';

/**
 * Hook personalizado para verificar acceso a características
 * basado en el plan de membresía del usuario
 */
export const useFeatureAccess = () => {
  const { 
    membershipStatus, 
    hasFeatureAccess, 
    isPremium, 
    isFree,
    loading,
    error 
  } = useMembership();
  const { role } = useDataUser();

  /**
   * Verificar si el usuario tiene acceso a una característica específica
   * @param {string} featureName - Nombre de la característica a verificar
   * @returns {boolean} - true si tiene acceso, false en caso contrario
   */
  const canAccess = (featureName) => {
    // Primero verificar con el contexto de membresía
    if (hasFeatureAccess(featureName)) {
      return true;
    }

    // Si no está en el contexto, verificar con la configuración local
    const currentPlan = isPremium ? 'premium' : 'free';
    const userRole = role || 'USER'; // Default a USER si no hay rol definido
    
    return checkFeatureAccess(userRole, currentPlan, featureName);
  };

  /**
   * Verificar si el usuario tiene acceso a múltiples características
   * @param {string[]} featureNames - Array de nombres de características
   * @returns {boolean} - true si tiene acceso a todas, false en caso contrario
   */
  const canAccessAll = (featureNames) => {
    if (loading || !membershipStatus) return false;
    return featureNames.every(feature => hasFeatureAccess(feature));
  };

  /**
   * Verificar si el usuario tiene acceso a al menos una característica
   * @param {string[]} featureNames - Array de nombres de características
   * @returns {boolean} - true si tiene acceso a al menos una, false en caso contrario
   */
  const canAccessAny = (featureNames) => {
    if (loading || !membershipStatus) return false;
    return featureNames.some(feature => hasFeatureAccess(feature));
  };

  /**
   * Obtener características disponibles para el plan actual
   * @returns {string[]} - Array de características disponibles
   */
  const getAvailableFeatures = () => {
    // Combinar características del contexto y configuración local
    const contextFeatures = membershipStatus?.features || [];
    const currentPlan = isPremium ? 'premium' : 'free';
    const userRole = role || 'USER';
    const configFeatures = getFeaturesForRoleAndPlan(userRole, currentPlan);
    
    // Combinar y eliminar duplicados
    return [...new Set([...contextFeatures, ...configFeatures])];
  };

  /**
   * Obtener características restringidas para el plan actual
   * @param {string[]} allFeatures - Lista completa de características
   * @returns {string[]} - Array de características restringidas
   */
  const getRestrictedFeatures = (allFeatures) => {
    const availableFeatures = getAvailableFeatures();
    return allFeatures.filter(feature => !availableFeatures.includes(feature));
  };

  /**
   * Obtener características basadas en el rol del usuario
   * @returns {string[]} - Array de características para el rol actual
   */
  const getRoleBasedFeatures = () => {
    const userRole = role || 'USER';
    const currentPlan = isPremium ? 'premium' : 'free';
    return getFeaturesForRoleAndPlan(userRole, currentPlan);
  };

  return {
    // Estado
    membershipStatus,
    loading,
    error,
    
    // Verificaciones de plan
    isPremium,
    isFree,
    
    // Verificaciones de características
    canAccess,
    canAccessAll,
    canAccessAny,
    
    // Información de características
    getAvailableFeatures,
    getRestrictedFeatures,
    getRoleBasedFeatures,
    
    // Información del rol
    role
  };
};

export default useFeatureAccess; 